(function AdzukiFileUploadForm($, Adzuki)
{
	const component = {
		emits: [
			'update-media',
			'selected-media',
			'upload-all',
			'cancel',
			'cancel-all',
			'crop-image',
			'existing-image',
			'pop-form',
			'pending-approval',
			'loading'
		],
		inject: {
			formatDuration: {
				default: () => undefined
			},
			formatFileSize: {
				default: () => undefined
			},
			fileIcon: {
				default: () => undefined
			},
			formatDate: {
				default: () => undefined
			},
			getSectionInfo: {
				default: () => undefined
			},
			openToast: {
				default: () => undefined
			},
			openAlert: {
				default: () => undefined
			},
			normalizeExtension: {
				default: () => undefined
			},
			sendMediaToParent: {
				default: () => undefined
			}
		},
		props: {
			media: {
				type   : Object,
				default: () => {
					return {
						id            : null,
						title         : '',
						description   : '',
						tags          : [],
						isFavorite    : false,
						isGlobal      : false,
						shareFranchise: false,
					};
				}
			},
			file: {
				type   : Object,
				default: () => ({
					name        : null,
					size        : null,
					extension   : null,
					previewUrl  : null,
					filePath    : null,
					originalFile: null,
					captions    : null,
					captionsUrl : null,
					type        : null,
					width       : null,
					height      : null,
					fileOrigin  : null,
					thumbnail   : null,
					thumbPath   : null,
				})
			},
			index: {
				type   : Number,
				default: null,
			},
			fileCount: {
				type   : Number,
				default: 0,
			},
			sectionInfo: {
				type: Object,
			},
			uploadParams: {
				type: Object,
			}
		},
		template: '#adzuki-file-upload-form',
		data() {
			return {
				suggestions           : [],
				showSuggestions       : false,
				tagInputValue         : '',
				// a new tag that looks like an existing one, waiting for Use or Add anyway (MEDIA-312)
				tagDuplicates         : null,
				fileForm              : {},
				uploadProps           : null,
				existingFile          : null,
				createCopy            : false,
				isLoading             : false,
				showConfirm           : false,
				confirmMessage        : '',
				clientSuggestions     : [],
				showClientSuggestions : false,
				clientSearchInput     : '',
				clientSearchMinLength : 2,
				debouncedSearchClients: null,
			};
		},
		created() {
			const search = (term) => {
				this.searchClients(term);
			};

			this.debouncedSearchClients = Adzuki.Debounce(search, 300);
		},
		computed: {
			hasResizeDimensions() {
				return Number(this.sectionInfo.resize_width) > 0 && Number(this.sectionInfo.resize_height) > 0;
			}
		},
		watch: {
			// typing again drops the look-alike prompt (MEDIA-312)
			tagInputValue(value) {
				if (this.tagDuplicates && String(value || '').trim() !== this.tagDuplicates.name) {
					this.tagDuplicates = null;
				}
			},
			isLoading: {
				handler(value) {
					this.$emit('loading', value);
				},
				flush: 'sync'
			}
		},
		methods: {
			// already the section's shape (within 1%) and at least its size, so no crop is needed
			// before upload (MEDIA-287); anything else still opens the fixed-ratio cropper
			fitsSectionSize(file) {
				const width        = Number(file && file.width);
				const height       = Number(file && file.height);
				const targetWidth  = Number(this.sectionInfo.resize_width);
				const targetHeight = Number(this.sectionInfo.resize_height);

				if (!width || !height || !targetWidth || !targetHeight) {
					return false;
				}

				const targetRatio = targetWidth / targetHeight;
				const offBy       = Math.abs((width / height) - targetRatio) / targetRatio;

				return offBy <= 0.01 && width >= targetWidth && height >= targetHeight;
			},
			determineFileUpload() {
				this.isLoading = true;
				if (this.file.type === 'image') {
					if (this.hasResizeDimensions && !this.fitsSectionSize(this.file)) {
						this.isLoading = false;
						this.$emit('crop-image', this.file);
					} else {
						this.uploadFile();
					}
				} else {
					this.uploadFile();
				}
			},
			getVideoDuration(item) {
				const existing = Number(item && item.duration);

				if (Number.isFinite(existing) && existing > 0) {
					return Promise.resolve(Math.floor(existing));
				}

				return new Promise((resolve, reject) => {
					const video = document.createElement('video');
					video.preload = 'metadata';
					video.muted   = true;

					const canUseFile = item && item.originalFile instanceof File;
					const src        = canUseFile ? URL.createObjectURL(item.originalFile) : item && item.previewUrl;

					if (!src) {
						reject(new Error('No video source available'));
						return;
					}

					let settled = false;
					const cleanupAnd = (fn) => {
						if (settled) {
							return;
						}

						settled = true;

						video.onloadedmetadata = null;
						video.onerror = null;
						video.onseeked = null;

						try {
							video.removeAttribute('src');
						} catch (e) {
							console.error('Error removing video src attribute:', e);
						}

						try {
							video.load();
						} catch (e) {
							console.error('Error loading video:', e);
						}

						if (canUseFile) {
							try {
								URL.revokeObjectURL(src);
							} catch (e) {
								console.error('Error revoking video URL:', e);
							}
						}

						fn();
					};

					video.onerror = () => cleanupAnd(() => reject(new Error('Failed to load video metadata')));

					video.onloadedmetadata = () => {
						// Some browsers report Infinity until you seek
						const finish = () => {
							const d        = Number(video.duration);
							const duration = (Number.isFinite(d) && d > 0) ? Math.floor(d) : null;
							cleanupAnd(() => resolve(duration));
						};

						if (!Number.isFinite(video.duration) || video.duration <= 0) {
							video.onseeked = finish;
							try {
								video.currentTime = 1e101;
							} catch (e) {
								finish();
							}
							return;
						}

						finish();
					};

					video.src = src;
				});
			},
			uploadFile(createCopy = false) {
				this.isLoading = true;

				let url = this.$baseUri + '/Media/saveMediaHubFile/';

				const mediaHubItem = new FormData();

				mediaHubItem.append('title', this.fileForm.title);
				mediaHubItem.append('description', this.fileForm.description);
				mediaHubItem.append('tags', JSON.stringify(this.fileForm.tags));
				mediaHubItem.append('favorite', this.fileForm.isFavorite ? 1 : 0);
				mediaHubItem.append('global', this.fileForm.isGlobal ? 1 : 0);
				mediaHubItem.append('share_franchise', this.fileForm.shareFranchise ? 1 : 0);

				if (this.fileForm.id) {
					url = this.$baseUri + '/Media/updateMediaItem/';
					mediaHubItem.append('id', this.fileForm.id);
				}

				const hasNewFile = this.file && (this.file.originalFile || this.file.filePath);

				if (hasNewFile) {
					mediaHubItem.append('file', this.file.originalFile);
					mediaHubItem.append('file_path', this.file.filePath);
					mediaHubItem.append('file_origin', this.file.fileOrigin || null);
					mediaHubItem.append('type', this.file.type);
					mediaHubItem.append('extension', this.file.extension);
					mediaHubItem.append('size', this.file.size);

					if (this.file.type === 'image') {
						mediaHubItem.append('height', this.file.height || null);
						mediaHubItem.append('width', this.file.width || null);
					}

					if (this.file.type === 'video') {
						mediaHubItem.append('thumbnail', this.file.thumbnail || null);

						if (this.file.thumbPath) {
							mediaHubItem.append('thumb_path', this.file.thumbPath);
						}

						if (this.file.captions) {
							mediaHubItem.append('captions', this.file.captions);
						}

						if (this.fileForm.client_id) {
							mediaHubItem.append('client_id', this.fileForm.client_id);
						}
					}
				}

				if (this.$section) {
					mediaHubItem.append('section', this.$section);
				} else if (this.sectionInfo && this.sectionInfo.internal_name) {
					mediaHubItem.append('section', this.sectionInfo.internal_name);
				}

				// Pass the effective needs_approval (after URL override on sectionInfo)
				// so the server can overwrite the DB section flag for this request.
				if (typeof this.$needsApproval === 'boolean') {
					mediaHubItem.append('needs_approval', Number(this.sectionInfo?.needs_approval) === 1 ? '1' : '0');
				}

				if (createCopy === true) {
					mediaHubItem.append('create_copy', 'true');
				}
				const send = () => {
					fetch(url, {
						method: 'POST',
						body  : mediaHubItem,
					})
						.then(response => response.json())
						.then(data => {
							console.log('Upload File Response', data);

							if (data.status === 'existing_asset') {
								this.existingFile = data.existing_asset;

								this.uploadProps = {
									file : this.existingFile.file,
									asset: this.existingFile.asset,
									media: this.existingFile.media
								};

								console.log('Emit Existing File', this.uploadProps);

								if (this.sectionInfo) {
									this.isLoading = false;
									this.$emit('existing-image', this.uploadProps);
								} else {
									this.isLoading = false;
									this.componentToRender = Adzuki.VueComponents.AdzukiExistingFile;
								}

								return;
							}

							if (data.status === 'too_many_files') {
								let msg = data.too_many_files;

								if (data.filename) {
									msg += `<br><ul><li>${data.filename}</li></ul>`;
								}

								this.openAlert('error', 'Too Many Files', msg);
								this.isLoading = false;
								return;
							}

							// e.g. tos_required (MEDIA-302); never pass an error on as uploaded media
							if (data.status === 'error') {
								const failMessage = data.message || 'The file could not be uploaded.';

								if (typeof this.openAlert === 'function') {
									this.openAlert('error', 'Upload Failed', failMessage);
								} else {
									Adzuki.StatusMessage('error', failMessage, 'Upload Failed');
								}

								this.isLoading = false;
								return;
							}

							if (data.status === 'pending') {
								this.isLoading = false;

								const needsPopForm = Number(this.sectionInfo?.needs_approval) === 1 ||
									Number(data.matches_found) === 1;

								if (needsPopForm) {
									this.$emit('pending-approval', {
										...data,
										file: this.file
									});
								} else {
									this.openAlert('success', 'Success', data.message);
									this.$emit('cancel', this.file);
								}

								return;
							}

							if (this.sectionInfo) {
								this.isLoading = false;

								if (this.sectionInfo.vue_app === 1) {
									this.$emit('selected-media', data);
									return;
								}

								this.sendMediaToParent(data);
								return;
							}

							if (this.index === null) {
								this.isLoading = false;
								this.$emit('update-media', data.media);
							} else {
								this.isLoading = false;
								this.openAlert('success', 'Success', 'File uploaded successfully');
								this.$emit('cancel', this.file);
							}
						})
						.catch(error => {
							console.error('Error:', error);
						});
				};

				if (this.file && this.file.type === 'video') {
					const d = Number(this.file.duration);
					const hasDuration = Number.isFinite(d) && d > 0;

					if (hasDuration) {
						mediaHubItem.append('duration', Math.floor(d));
						send();
						return;
					}

					this.getVideoDuration(this.file)
						.then(duration => {
							if (duration !== null) {
								mediaHubItem.append('duration', duration);
							}
						})
						.catch(err => {
							console.error('Error getting video duration:', err);
						})
						.finally(() => {
							send();
						});

					return;
				}

				send();
			},
			uploadAllFiles() {
				this.isLoading = true;
				this.$emit('upload-all');
			},
			cancelAllFiles() {
				this.confirmMessage = 'Do you wish to cancel these file uploads?';
				this.confirmAction = 'cancelAllFiles';
				this.openCancelConfirm();
			},
			async searchTags(value) {
				if (!value || value.length === 0) {
					this.suggestions     = [];
					this.showSuggestions = false;
					return;
				}

				try {
					const formData = new FormData();
					formData.append('term', value);

					// Make a POST request to the backend
					const response = await fetch(this.$baseUri + '/MediaTag/searchTags', {
						method: 'POST',
						body  : formData,
					});

					if (!response.ok) {
						throw new Error(`Error while fetching categories: ${response.statusText}`);
					}

					const data = await response.json();

					if (Array.isArray(data)) {
						this.suggestions     = data;
						this.showSuggestions = true;
					} else {
						console.error('Unexpected data format from server:', data);
						this.suggestions     = [];
						this.showSuggestions = true;
					}

				} catch (err) {
					console.error('Error in searchTags:', err.message);
					this.suggestions = [];
				}
			},
			// a name that looks like one of the agent's or a global tag asks first:
			// Use the existing tag, or Add anyway (MEDIA-312)
			async addTag(confirmed) {
				const name = String(this.tagInputValue || '').trim();

				if (!name) {
					return;
				}

				if (confirmed !== true) {
					const tags    = await Adzuki.MediaTagDuplicates.loadTags(this.$baseUri);
					const matches = Adzuki.MediaTagDuplicates.similarTo(name, tags);

					if (matches.length) {
						this.tagDuplicates   = {
							name   : name,
							matches: matches,
							note   : Adzuki.MediaTagDuplicates.describe(name, matches),
						};
						this.showSuggestions = false;
						return;
					}
				}

				this.tagDuplicates = null;

				const formData = new FormData();
				formData.append('name', name);

				fetch(this.$baseUri + '/MediaTag/manageTag/', {
					method: 'POST',
					body  : formData,
				})
					.then(response => response.json())
					.then(data => {
						if (data && data.error) {
							this.openAlert('error', 'Error', data.error);
							return;
						}

						Adzuki.MediaTagDuplicates.remember(data);
						this.selectTag(data);
					})
					.catch(error => {
						this.openAlert('error', 'Error', error);
					});
			},
			// pick the existing tag instead of creating a look-alike (MEDIA-312)
			useExistingTag(tag) {
				if (!this.fileForm.tags.some(picked => String(picked.id) === String(tag.id))) {
					this.fileForm.tags.push(tag);
				}

				this.tagDuplicates   = null;
				this.showSuggestions = false;
				this.suggestions     = [];
				this.tagInputValue   = '';
			},
			selectTag(tag) {
				if (!this.fileForm.tags.includes(tag)) {
					this.fileForm.tags.push(tag);
				}
				this.showSuggestions = false;
				this.suggestions     = [];
				this.tagInputValue   = '';
			},
			removeTag(tagIndex) {
				this.fileForm.tags.splice(tagIndex, 1);
			},
			clientSuggestionLabel(client) {
				if (!client || typeof client !== 'object') {
					return '';
				}

				const resultName = client.result_name;
				const fullName = client.full_name;
				const name = client.name;

				if (typeof resultName === 'string' && resultName.length > 0) {
					return resultName;
				}

				if (typeof fullName === 'string' && fullName.length > 0) {
					return fullName;
				}

				if (typeof name === 'string' && name.length > 0) {
					return name;
				}

				return '';
			},
			handleClientSearchInput(event) {
				if (this.fileForm.client_id) {
					return;
				}

				const value = event && event.target ? event.target.value : '';
				const term = String(value).trim();

				if (term.length < this.clientSearchMinLength) {
					this.clientSuggestions = [];
					this.showClientSuggestions = false;

					return;
				}

				this.debouncedSearchClients(term);
			},
			async searchClients(term) {
				if (!term || term.length < this.clientSearchMinLength) {
					this.clientSuggestions = [];
					this.showClientSuggestions = false;

					return;
				}

				try {
					const response = await fetch(this.$baseUri + '/Clients/search/' + encodeURIComponent(term));

					if (!response.ok) {
						throw new Error(`Error while searching clients: ${response.statusText}`);
					}

					const data = await response.json();

					if (!Array.isArray(data)) {
						this.clientSuggestions = [];
						this.showClientSuggestions = false;

						return;
					}

					this.clientSuggestions = data.filter((item) => {
						return item && item.id !== undefined && item.id !== null && String(item.id).length > 0;
					});

					this.showClientSuggestions = this.clientSuggestions.length > 0;
				} catch (err) {
					this.clientSuggestions = [];
					this.showClientSuggestions = false;
				}
			},
			selectClient(client) {
				if (!client || !client.id) {
					return;
				}

				this.fileForm.client_id    = client.id;
				this.clientSearchInput     = this.clientSuggestionLabel(client);
				this.clientSuggestions     = [];
				this.showClientSuggestions = false;
			},
			resetClient() {
				this.fileForm.client_id    = null;
				this.clientSearchInput     = '';
				this.clientSuggestions     = [];
				this.showClientSuggestions = false;
			},
			cancelUpload() {
				this.confirmMessage = 'Do you wish to cancel this file upload?';
				this.confirmAction = 'cancelUpload';
				this.openCancelConfirm();
			},
			// same confirm Trip Summary uses (MEDIA-265)
			openCancelConfirm() {
				Adzuki.CustomConfirm({
					title  : 'Decision Time',
					content: this.confirmMessage,
					icon   : Adzuki.FaIcon.getIconStyle('warning'),
					confirm: () => this.confirmAccept(),
				});
			},
			confirmAccept() {
				this.showConfirm = false;

				switch (this.confirmAction) {
					case 'cancelUpload':
						this.$emit('cancel', this.file);
						this.openAlert('success', 'Success', 'File upload has been cancelled');
						break;
					case 'cancelAllFiles':
						this.$emit('cancel-all');
						this.openAlert('success', 'Success', 'All file uploads have been cancelled');
						break;
					default:
						break;
				}
			},
			closeModal() {
				this.existingFile = null;
			},
			emittedComponentData(fileData) {
				switch (fileData.type) {
					case 'cancel':
						this.cancelUpload();
						break;
					case 'upload':
						this.uploadFile(true);
						break;
					case 'select':
						console.log('Select existing file', fileData);
						break;
					default:
						break;
				}
			},
			continueUpload() {
				this.existingFile = false;

				this.uploadFile(true);
			},
			cancelExistingUpload() {
				this.existingFile = false;
			},
			closeEdit() {
				this.$emit('cancel', this.file);
			}
		},
		mounted() {
			this.fileForm = {
				id            : this.media.id || null,
				description   : this.media.description || '',
				tags          : this.media.media_tags || [],
				isFavorite    : Boolean(Number(this.media.favorite)) || false,
				isGlobal      : Boolean(Number(this.media.global)) || false,
				shareFranchise: Boolean(Number(this.media.share_franchise) || false),
				client_id     : this.media.client_id !== null && this.media.client_id !== '' ? this.media.client_id : null,
			};

			if (this.$franchiseId === '1') {
				this.fileForm.isGlobal = true;
			}

			const mediaTitle = this.media.title;
			const fileName   = this.file && this.file.name;

			if (typeof mediaTitle === 'string' && mediaTitle.length > 0) {
				this.fileForm.title = mediaTitle;
			} else if (typeof fileName === 'string' && fileName.length > 0) {
				this.fileForm.title = fileName;
			} else {
				this.fileForm.title = '';
			}

			if (this.fileForm.client_id) {
				this.clientSearchInput = this.clientSuggestionLabel(this.media);
			}
		}
	};


	Adzuki.VueComponents.AdzukiFileUploadForm = component;
}(jQuery, window.Adzuki || {}));
